/**
 * Xenial portal data service
 */
import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { forEach, get, isEmpty, map, merge } from 'lodash';

import { Observable, observableThrowError, rx } from '@shared/rx';
import {
  IApiListResponse,
  IBaseEntityType,
  IFilterQuery,
  IQuantityOnHandRequestParams,
  IQuantityOnHandResponse,
} from '@shared/types';
import { environment } from '../../../../environments/environment';
import { lazyInject } from '@app/shared/decorators';
import { MAX_PAGE_SIZE } from '@config/global-constants';

@Injectable()
export class XviDataService {
  @lazyInject(HttpClient) public http: HttpClient;

  public static UTILITY_TYPE: string = 'XviDataService';
  public defaultQuery: IFilterQuery = {};
  public canStartingCount: boolean = true;

  private get baseUrl(): string {
    return environment.xviBackendURI;
  }

  public getStandWorkSheet(
    userQuery?: IFilterQuery,
  ): Observable<IBaseEntityType[]> {
    const query: IFilterQuery = merge({}, this.defaultQuery, userQuery);
    return this.http
      .get(`${this.baseUrl}/stand-worksheet`, {
        params: this.createParams(query),
      })
      .pipe(
        this.catchHttpErrorAndPickErrorList(),
        rx.map((response: IApiListResponse<IBaseEntityType>) =>
          get(response, 'items', []),
        ),
      );
  }

  public getTransfer(userQuery?: IFilterQuery): Observable<IBaseEntityType[]> {
    const query: IFilterQuery = merge({}, this.defaultQuery, userQuery);
    return this.http
      .get(`${this.baseUrl}/transfer`, {
        params: this.createParams(query),
      })
      .pipe(
        this.catchHttpErrorAndPickErrorList(),
        rx.map((response: IApiListResponse<IBaseEntityType>) =>
          get(response, 'items', []),
        ),
      );
  }

  public getSaleDepletionOrder(
    userQuery?: IFilterQuery,
  ): Observable<IBaseEntityType[]> {
    const query: IFilterQuery = merge({}, this.defaultQuery, userQuery);
    return this.http
      .get(`${this.baseUrl}/sale-depletion-order`, {
        params: this.createParams(query),
      })
      .pipe(
        this.catchHttpErrorAndPickErrorList(),
        rx.map((response: IApiListResponse<IBaseEntityType>) =>
          get(response, 'items', []),
        ),
      );
  }

  public fetchQuantityOnHand(
    items: IBaseEntityType[],
    site_id: string,
    path: string = 'entity_id',
  ): Observable<IQuantityOnHandResponse> {
    const url = `${this.baseUrl}/quantity-on-hand/search`;
    const requestData: IQuantityOnHandRequestParams = {
      items: [
        {
          site_id,
          inventory_item_entity_ids: map(items, path),
        },
      ],
    };
    const userQuery = {
      select: ['count', 'entity_id', 'inventory_item_entity_id'],
      top: MAX_PAGE_SIZE,
    };
    const query: IQuantityOnHandRequestParams = merge({}, requestData);
    const params: HttpParams = this.createParams(userQuery);

    return this.http
      .put<IQuantityOnHandResponse>(url, query, { params })
      .pipe(this.catchHttpErrorAndPickErrorList());
  }

  public createParams({
    filter,
    select: selectArg,
    top,
    skip,
    orderby,
    expand,
    effectiveDate,
    include_entities,
    include_nested_type,
    customParams,
    include_items,
    include_calculated_values,
    ...queryParams
  }: IFilterQuery = {}): HttpParams {
    let params = new HttpParams();

    if (filter) {
      params = params.set('$filter', filter);
    }
    if (!isEmpty(selectArg)) {
      params = params.set('$select', selectArg.toString());
    }
    if (top) {
      params = params.set('$top', top.toString());
    }
    if (skip) {
      params = params.set('$skip', skip.toString());
    }
    if (orderby) {
      params = params.set('$orderby', orderby.toString());
    }
    forEach(queryParams, (param, key) => {
      params = params.set(key, param.toString());
    });
    if (include_entities) {
      const entityList: string = include_entities
        .map((entityType) => `'${entityType}'`)
        .join(',');
      params = params.set('include_entities', `[${entityList}]`);
    }
    if (include_nested_type) {
      const typeList: string = include_nested_type
        .map((type) => `'${type}'`)
        .join(',');
      params = params.set('include_nested_type', `[${typeList}]`);
    }
    if (customParams) {
      customParams.map(({ key, value }) => {
        params = params.set(key, String(value));
      });
    }
    if (expand) {
      params = params.set('$expand', expand.toString());
    }
    if (effectiveDate) {
      params = params.set('effective_date', effectiveDate);
    }
    if (include_items) {
      params = params.set('$include_items', include_items.toString());
    }
    if (include_calculated_values) {
      params = params.set(
        '$include_calculated_values',
        include_calculated_values.toString(),
      );
    }

    return params;
  }

  private catchHttpErrorAndPickErrorList =
    (): any =>
    <T>(input$: Observable<T>): Observable<T> =>
      input$.pipe(
        rx.catchError(({ error }: HttpErrorResponse) =>
          observableThrowError(get(error, 'errors')),
        ),
      );
}
